import { fabric } from 'fabric'
import BaseHandler from './BaseHandler'
import { HandlerOptions } from '../common/interfaces'
import { FrameOptions } from '../objects'
import { loadImageFromURL } from '../utils/image-loader'
import { nanoid } from 'nanoid'
import { BASE_FRAME_HEIGHT, BASE_FRAME_WIDTH, ObjectType } from '../common/constants'
import { MediaImageRepository } from '../objects/media-repository/media_image_repository'
import { debounce } from 'lodash'
import store from '@/store/store'
import { ActionType } from '@/store/slices/editor/actionTypes'
import { findSizeId } from '@/components/ResizePanel/findSizeId'
import { convertUnitValue as _convertUnitValue } from '@/utils/unit'
import { CanvasBlur } from '../objects/CanvasBlur.filter'
import { lightTheme } from '@/customTheme'
import Icons from '@/scenes/Editor/components/Icons'
import api from '@/services/api'
import { FabricObject } from '@/common/interfaces'
import { LayoutUtils } from '@/utils/LayoutUtils'
import CanvasImageRenderer from '../utils/canvasImageRenderer'
import { createDefaultLayer } from '../utils/utils'
import { ResizeContentProps } from '@/scenes/Editor/components/Panels/PanelItems/types'
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle"
import { Size } from "@/shared/sync/mediaRepository/size"
import { Point } from "@/shared/sync/mediaRepository/point"
import { MediaImageType } from '@/shared/sync/mediaRepository/media_image_type'
import { MediaImageRepositoryProvider } from '../objects/media-repository/MediaImageRepositoryProvider'
import { selectDisableEditorState, selectIsOpenInspector } from '@/store/slices/editor/selector'

class FrameHandler extends BaseHandler {
  static DEFAULT_FRAME_DIMENSIONS: Pick<ResizeContentProps, 'size' | 'name' | 'unit'> = {
    size: {
      width: 2048,
      height: 2048,
    },
    name: 'Square',
    unit: 'px'
  }

  frame
  options
  sizeFormat
  backgroundimage
  private initIntervalId: ReturnType<typeof setInterval> | null = null
  private backgroundImageLoadController: AbortController | null = null

  // Padding values for calculating fit ratio
  SMALL_SCREEN_HEIGHT = 700
  HORIZONTAL_PADDING = 32 // single side padding
  HORIZONTAL_PADDING_SMALL_SCREEN = 32 // single side padding
  VERTICAL_PADDING = 60
  VERTICAL_PADDING_SMALL_SCREEN = 60
  
  DEFAULT_HEIGHT_EDIT_WITH_TEXTBOX = 97
  MARGIN_BOTTOM_WHEN_TEXTBOX_OPEN = 16
  EDIT_WITH_AI_MARGIN_TOP = 32
  baseFrameWidth
  baseFrameHeight
  baseFrameCenterX
  baseFrameCenterY
  originalObjectsStates = []

  get FRAME_PADDING_W() {
    if (window.innerHeight < this.SMALL_SCREEN_HEIGHT) {
      return this.HORIZONTAL_PADDING_SMALL_SCREEN * 2
    } else {
      return this.HORIZONTAL_PADDING * 2
    }
  }
  
  get FRAME_PADDING_H() {
    if (window.innerHeight < this.SMALL_SCREEN_HEIGHT) {
      return this.VERTICAL_PADDING_SMALL_SCREEN * 2
    } else {
      return this.VERTICAL_PADDING * 2
    }
  }

  get FRAME_PADDING_LEFT() {
    const isOpenInspector = selectIsOpenInspector(store.getState())
    const isOpenAddMenu = selectDisableEditorState(store.getState()).addMenu !== 'hidden'
    return LayoutUtils.getFramePaddingLeft(isOpenInspector, isOpenAddMenu)
  }

  get FRAME_PADDING_RIGHT() {
    const isOpenScene = selectDisableEditorState(store.getState()).scenes !== 'hidden'
    return LayoutUtils.getFramePaddingRight(isOpenScene)
  }

  get FRAME_PADDING_TOP() {
    const isOpenFloatingInspector = selectDisableEditorState(store.getState()).floatingInspector !== 'hidden'
    return LayoutUtils.getFramePaddingTop(isOpenFloatingInspector)
  }

  get FRAME_PADDING_BOTTOM() {
    // Keep margin the same when edit with ai is closed to avoid layout shift
    const isEditWithAiOpen = true // selectDisableEditorState(store.getState()).editWithAI !== 'hidden'
    return LayoutUtils.getFramePaddingBottom(isEditWithAiOpen)
  }

  get FRAME_CENTER_OFFSET_X() {
    return (this.FRAME_PADDING_LEFT - this.FRAME_PADDING_RIGHT) / 2
  }

  get FRAME_CENTER_OFFSET_Y() {
    return (this.FRAME_PADDING_TOP - this.FRAME_PADDING_BOTTOM) / 2
  }

  constructor(props: HandlerOptions) {
    super(props)
    // this.options = defaultFrameOptions
    this.initialize()
  }

  initialize() {
    const callbackFromCreationPage = store.getState().editor.editor.callbackFromCreationPage
    let frame_width = 2048
    let frame_height = 2048
    let frame_fill = lightTheme.colors.white
    let frame_strokeWidth = 0
    let sizeId
    let unit
    let icon

    if (callbackFromCreationPage) {
      const action = callbackFromCreationPage.action
      if (action === ActionType.CUSTOM_SIZE && callbackFromCreationPage.value) {
        frame_width = callbackFromCreationPage.value.width
        frame_height = callbackFromCreationPage.value.height
        sizeId = callbackFromCreationPage.value.sizeId
        unit = callbackFromCreationPage.value.unit
        icon = callbackFromCreationPage.value.icon
      } else {
        const size = store.getState().editor.uploads.uploads[0]?.size
        if (size?.width && size?.height) {
          frame_width = Math.round(size.width)
          frame_height = Math.round(size.height)
        }
      }
    }
    const frame = new fabric.Frame({
      width: frame_width,
      height: frame_height,
      id: '',
      name: 'Initial Frame',
      fill: frame_fill,
      hoverCursor: 'default',
      absolutePositioned: this.config.clipToFrame, // for clipPath
      // rx: 16,
      // ry: 16,
      strokeWidth: frame_strokeWidth,
      stroke: 'rgba(0, 0, 0, 0.15)',
      // @ts-ignore
      sizeId: sizeId === 'custom-size' ? 'custom-size' : findSizeId(frame_width, frame_height),
      unit: unit ? unit : 'px',
      icon: icon ? icon : null,
    })
    this.canvas.add(frame)
    frame.center()
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max (50 * 100ms)
    this.initIntervalId = setInterval(() => {
      attempts++
      if (this.root.scrollbarHandler && this.root.scrollbarHandler.updateScrollPosition) {
        this.root.zoomHandler.zoomToFit()
        this.root.scrollbarHandler.updateScrollPosition()
        frame.set({
          fill: lightTheme.colors.white,
          // rx: 16 / this.getFitRatio(),
          // ry: 16 / this.getFitRatio(),
        })
        if (this.initIntervalId) {
          clearInterval(this.initIntervalId)
          this.initIntervalId = null
        }
      } else if (attempts >= maxAttempts) {
        // Prevent infinite polling
        if (this.initIntervalId) {
          clearInterval(this.initIntervalId)
          this.initIntervalId = null
        }
      }
    }, 100)
  }

  destroy() {
    if (this.initIntervalId) {
      clearInterval(this.initIntervalId)
      this.initIntervalId = null
    }
  }

  get = () => {
    return this.canvas.getObjects().find(object => object.type === ObjectType.FRAME)
  }

  update = (
    options,
    { update = true, zoomToFit = true, ignoreAnimation = false, sceneId = null, createTransaction = true } = {},
  ) => {
    // this.sizeFormat = options
    let end = null
    if (update && createTransaction) {
      end = this.root.transactionHandler.save()
    }
    const frame = this.get() as fabric.Frame & {
      sizeId?: string,
      icon: any,
      unit: string
    }
    const newFrameWidth = options.width
    const newFrameHeight = options.height
    let hasSizeId = findSizeId(newFrameWidth, newFrameHeight)

    frame.set('width', newFrameWidth)
    frame.set('height', newFrameHeight)
    if (options.sizeId) {
      frame.set('sizeId', options.sizeId)
      if (options.icon) {
        frame.set('icon', options.icon)
      }
    } else if (hasSizeId) {
      let { sizeId, icon } = findSizeId(newFrameWidth, newFrameHeight, true)
      frame.set({
        sizeId: sizeId,
        icon: icon ? icon : null
      })
    } else {
      frame.set({
        sizeId: 'custom-size',
        icon: Icons.CustomSize
      })
    }
    if (update) {
      if (options.icon) {
        frame.set('icon', options.icon)
        frame.set('unit', options.unit)
      } else {
        frame.set('icon', null)
        frame.set('unit', 'px')
      }
      
    }
    this.root.pagesHandler?.updateSceneCanvasSize({
      width: newFrameWidth,
      height: newFrameHeight,
      sizeId: frame.sizeId,
      unit: frame.unit,
    }, sceneId);
    this.editorEventManager.emit('scenes:changed');

    if (zoomToFit) {
      this.root.zoomHandler.zoomToFit(ignoreAnimation)
    }

    frame.set({
      rx: 45 * Math.min(frame.width, frame.height) / BASE_FRAME_WIDTH,
      ry: 45 * Math.min(frame.width, frame.height) / BASE_FRAME_HEIGHT,
    })
    frame.center()
    if (update) {
      this.adjustLayers(newFrameWidth, newFrameHeight, sceneId)
      if (sceneId) {
        this.root.pagesHandler?.syncSceneFramePositionWithLiveFrame(sceneId)
      }
    }
    this.scaleImageBgFill()
    this.root.scrollbarHandler.handleFrameSizeChange()
    this.canvas.requestRenderAll()
    this.editorEventManager.emit('frame:changed')
    if (end){
      end()
    }
  }

  getFrameDimensionsInUnit = (): Pick<ResizeContentProps, 'size' | 'name' | 'unit'> | null => {
    const frame = this.get() as fabric.Frame & {
      sizeId?: string,
      unit?: string
    }
    if(!frame) {
      // Return default values when no frame exists
      return FrameHandler.DEFAULT_FRAME_DIMENSIONS
    }

    const frameUnit = frame.unit || 'px'
    let size = {
      width: frame.width,
      height: frame.height,
    }

    // If frame is not in pixels, convert from pixels to the frame's unit
    if (frameUnit !== 'px') {
      size = FrameHandler.convertUnitValue('px', frameUnit, size)
    }

    return {
      size,
      name: frame?.sizeId?.split('_')[1] ?? 'Custom size',
      unit: frameUnit,
    }
  }

  updateCanvasWithFrame = async (canvas, ignoreAnimation = false, zoomToFit = true) => {
    const currentFrame = this.get();
    const previousFrame = Object.assign({}, currentFrame);
    this.updateFrameSize(previousFrame, ignoreAnimation, zoomToFit);
    const { variationLeft, variationTop } = this.calculatePositionVariation(currentFrame, previousFrame);
    this.updateObjectsOnCanvas(canvas, currentFrame, variationLeft, variationTop);
    this.scaleImageBgFill();
    canvas.requestRenderAll();
  }

  updateFrameSize = async (previousFrame, ignoreAnimation = false, zoomToFit) => {
    await this.update(
      { width: previousFrame.width, height: previousFrame.height },
      { update: false, zoomToFit, ignoreAnimation },
    );
  }

  calculatePositionVariation = (currentFrame, previousFrame) => {
    const variationLeft = previousFrame.left - currentFrame.left;
    const variationTop = previousFrame.top - currentFrame.top;
    return { variationLeft, variationTop };
  }


  updateObjectsOnCanvas = (canvas, currentFrame, variationLeft, variationTop) => {
    if (variationLeft === 0 && variationTop === 0) {
      return
    }

    const currentSceneId = this.root.pagesHandler?.currentScene?.sceneId;

    canvas.forEachObject((obj: any) => {
      if (obj.type !== ObjectType.FRAME) {
        obj.clipPath = currentFrame;
      }
      if (obj.type === ObjectType.FRAME) {
        return obj;
      }
      
      // Only update objects belonging to current scene
      // Objects without keyFrames (legacy) appear on all scenes and should be updated
      if (currentSceneId && obj.keyFrames && Object.keys(obj.keyFrames).length > 0 && !obj.keyFrames[currentSceneId]) {
        return obj; // Skip objects not belonging to current scene
      }
      
      obj.left -= variationLeft;
      obj.top -= variationTop;
      obj.setCoords();
      return obj;
    });
  }

  setBackgroundColor = async (
    color: string,
    metadata?: { signal?: AbortSignal; targetSceneId?: string; targetSceneIds?: string[] }
  ) => {
    const { signal, targetSceneId, targetSceneIds } = metadata ?? {}
    this.backgroundImageLoadController?.abort()
    const colorImage = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.createColorImage(color, 100, 100)
    await this.setBackgroundImageURL(
      {latest: new URL(colorImage), thumbnail: new URL(colorImage)},
      {backgroundIdentifier: color, source_platform: "color", abortSignal: signal, targetSceneId, targetSceneIds}
    )
  }

  setBackgroundImageURL = async (
    urls: { latest: URL, thumbnail: URL },
    metadata: {
      backgroundIdentifier: string
      source_platform: string
      abortSignal?: AbortSignal
      updateTransaction?: boolean
      targetSceneId?: string
      targetSceneIds?: string[]
      /** When true, skip shared load controller so parallel `applyBackgroundToAllPages` calls do not abort each other. */
      parallelBatch?: boolean
    },
  ) => {
    // Capture scene ID at the start to prevent race conditions when user switches scenes
    const {
      backgroundIdentifier,
      source_platform,
      abortSignal,
      updateTransaction = false,
      targetSceneId = this.root.pagesHandler?.currentScene?.sceneId ?? null,
      targetSceneIds,
      parallelBatch = false,
    } = metadata

    if (targetSceneIds && targetSceneIds.length > 0) {
      let end = null
      if (updateTransaction && !abortSignal?.aborted) {
        end = this.root.transactionHandler.save()
      }
      await Promise.all(
        targetSceneIds.map(id =>
          this.setBackgroundImageURL(urls, { backgroundIdentifier, source_platform, abortSignal, updateTransaction: false, targetSceneId: id, parallelBatch: true })
        )
      )
      if (end) end()
      return
    }
    const { latest, thumbnail } = urls
    const operationAbortController = new AbortController()
    if (!parallelBatch) {
      this.backgroundImageLoadController?.abort()
      this.backgroundImageLoadController = operationAbortController
    }
    // Forward external abort → internal controller so downstream awaits only need
    // to observe operationAbortController.signal (single source of truth).
    if (abortSignal) {
      if (abortSignal.aborted) {
        operationAbortController.abort()
      } else {
        abortSignal.addEventListener('abort', () => operationAbortController.abort(), { once: true })
      }
    }
    const internalSignal = operationAbortController.signal
    const isAborted = () => internalSignal.aborted

    let end = null;
    if (updateTransaction && !isAborted()) {
      end = this.root.transactionHandler.save()
    }

    const guid = nanoid()
    const assetStateId = nanoid()
    const thumbnailLayer = await this.setBackgroundImage({guid, assetStateId, imageUrl: thumbnail, backgroundIdentifier, source_platform}, {abortSignal: internalSignal, targetSceneId})
    // Skip preview emit if this load was superseded (e.g. user clicked Transparent)
    // to avoid flashing a stale thumbnail.
    if (!isAborted()) {
      this.editorEventManager.emit('background:preview:changed', {})
    }
    if (!isAborted() && thumbnailLayer && this.allScenesHaveBackground()) {
      this.removeTransparentBg()
    }
    if (thumbnailLayer) {
      MediaImageRepositoryProvider.getEditingProjectInstance().storeImageUrl(
        guid,
        assetStateId,
        MediaImageType.thumbnail,
        thumbnail,
        internalSignal
      )
    }


    setTimeout(async () => {
      if (isAborted()) {
        // Close the transaction even when aborted so a subsequent
        // setTransparentBg/setBackgroundColor can open a clean one
        // (prevents leaked/stacked transactions on undo).
        end?.()
        return
      }
      let backgroundLayer = await this.setBackgroundImage({guid, assetStateId, imageUrl: latest, backgroundIdentifier, source_platform}, {abortSignal: internalSignal, targetSceneId})
      if (!backgroundLayer) {
        end?.()
        return
      }
      this.editorEventManager.emit('background:changed', {})
      await MediaImageRepositoryProvider.getEditingProjectInstance().storeImageUrl(
        guid,
        assetStateId,
        MediaImageType.latest,
        latest
      )


      setTimeout(async () => {
        // Bail out of expensive post-processing if superseded — downstream
        // stores/masks are no longer relevant.
        if (isAborted()) {
          end?.()
          return
        }
        let resizeLatestBlob = await MediaImageRepositoryProvider.getEditingProjectInstance().getDataBlob(latest);
        let base64Image = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.resizeBase64ToMaxEdgeSize(resizeLatestBlob, 1280);

        let promiseStoreOriginal = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(
          guid,
          assetStateId,
          MediaImageType.original,
          base64Image
        )

        let promiseStoreFullRes = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(
          guid,
          assetStateId,
          MediaImageType.fullRes,
          base64Image
        )

        let maskInfo = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.extractMask(base64Image);
        let promiseStoreMask = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(
          guid,
          assetStateId,
          MediaImageType.mask,
          maskInfo.base64
        )

        let size = this.getSize()
        if (size.aspectRatio() > 1) {
          size = new Size(1280, 1280 / size.aspectRatio());
        } else {
          size = new Size(1280 * size.aspectRatio(), 1280);
        }

        let result = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.aspectFitImage(base64Image, size);
        let image = base64Image;
        let boundingBox = new Rectangle(0, 0, 1, 1);
        if (result) {
          image = result.image;
          boundingBox = result.boundingBox;
        }

        let thumbnail = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.resizeBase64ToMaxEdgeSize(image, 256);
        
        let promiseFitted = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageUrl(
          guid,
          assetStateId,
          MediaImageType.fitted,
          image as unknown as URL
        )

        let promiseLatest = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageUrl(
          guid,
          assetStateId,
          MediaImageType.latest,
          image as unknown as URL
        )

        let promiseThumbnail = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageBase64(
          guid,
          assetStateId,
          MediaImageType.thumbnail,
          thumbnail
        )

        let roi = boundingBox.multiply(maskInfo.size);
        let maskImage = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(maskInfo.base64);
        let croppedMask = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.cropHtmlImage(maskImage, roi);
        let promiseFittedMask = MediaImageRepositoryProvider.getEditingProjectInstance().storeImageUrl(
          guid,
          assetStateId,
          MediaImageType.fittedMask,
          croppedMask.src as unknown as URL
        )

        let setBoundingBoxPromise = new Promise((resolve, reject) => {
          
          if (backgroundLayer){
            // @ts-ignore
            backgroundLayer.boundingBox = boundingBox;
          }
          resolve(true);
        });
        Promise.all([promiseStoreOriginal, promiseStoreFullRes, promiseStoreMask, promiseFitted, promiseLatest, promiseFittedMask, promiseThumbnail, setBoundingBoxPromise]).then(() => {
          // Always close the transaction (even if aborted mid post-processing)
          // so a subsequent load/color/transparent can open a clean one.
          end?.()
          console.log('finished generating bg assets');
        });
      })
    });
  }
  
  setTransparentBg = async (update = true, ignoreUnsetBackground = false) => {
    this.backgroundImageLoadController?.abort()
    let end = null;
    if (update) {
      end = this.root.transactionHandler.save()
    }
    if(!ignoreUnsetBackground) {
      // Apply transparent bg to all scenes
      const selectedScenesIds = this.root.pagesHandler?.selectedSceneIds
      await Promise.all(
        selectedScenesIds.map((sceneId) => {
          return this.unsetBackgroundImage(sceneId)
        }),
      )
    }
    const frame = this.get()
    const currentZoom = this.canvas.getZoom()
    const squareSize = 14
    const newTransparent = function () {
      const chessStatic = new fabric.StaticCanvas(null);
      try {
        chessStatic.setHeight(squareSize * 2);
        chessStatic.setWidth(squareSize * 2);
        chessStatic.renderAll();
        chessStatic.setBackgroundColor('white', () => { });

        const greyRect1 = new fabric.Rect({
          width: squareSize,
          height: squareSize,
          left: 0,
          top: 0,
          strokeWidth: 0
        });
        greyRect1.set('fill', lightTheme.colors.grayScale150)

        const greyRect2 = new fabric.Rect({
          width: squareSize,
          height: squareSize,
          left: squareSize,
          top: squareSize,
          strokeWidth: 0
        });
        greyRect2.set('fill', lightTheme.colors.grayScale150)

        chessStatic.add(greyRect1);
        chessStatic.add(greyRect2);
        chessStatic.calcOffset();
        chessStatic.renderAll();

        const canvasDataUrl = chessStatic.toDataURL({ quality: 1 })
        const imageElement = new Image()
        imageElement.src = canvasDataUrl
        const transparentPattern = new fabric.Pattern({
          source: imageElement,
          repeat: 'repeat',
          patternTransform: [1 / currentZoom, 0, 0, 1 / currentZoom, 0, 0]
        });
        if (transparentPattern.id === 0 || transparentPattern.id === undefined) {
          transparentPattern.id = Date.now()
        }
        return transparentPattern;
      } finally {
        chessStatic.dispose()
      }
    }
    frame.set({
      fill: newTransparent(),
      // @ts-ignore
      isTransparentBg: true,
    })
    this.canvas.requestRenderAll()
    this.editorEventManager.emit('frame:transparent')
    this.editorEventManager.emit('background:changed', {})
    if (end){
      end()
    }
  }

  hasTransparentBg = (sceneId?: string | null): boolean => {
    const frame = this.get()
    const backgroundImage = this.getBackgroundImage(sceneId ?? this.root.pagesHandler?.currentScene?.sceneId)
    // @ts-ignore
    return !!(frame && frame.fill && frame.fill.id) && !backgroundImage
  }

  private allScenesHaveBackground(): boolean {
    const scenes = this.root.pagesHandler?.scenes
    if (!scenes || scenes.length === 0) return false
    return scenes.every(scene => !!this.getBackgroundImageForScene(scene.sceneId))
  }

  removeTransparentBg = () => {
    const frame = this.get()
    // @ts-ignore
    if (frame.fill && frame.fill.id) {
      frame.set({
        fill: lightTheme.colors.white,
        // @ts-ignore
        isTransparentBg: false,
      })
    }
  }

  async setBackgroundImage(
    payload: {  guid: string
      assetStateId: string
      imageUrl: URL | string
      backgroundIdentifier: string
      source_platform?: string}, 
    options: { abortSignal?: any, targetSceneId?: string }): Promise<fabric.Object | null> {
    const { guid, assetStateId, imageUrl, backgroundIdentifier, source_platform } = payload
    const { abortSignal, targetSceneId } = options
    // Capture scene ID at the start before any async operations to prevent race conditions
    // when user switches scenes while image is loading
    const sceneId = targetSceneId ?? this.root.pagesHandler?.currentScene?.sceneId ?? null;
    
    let image: any = null
    const frame = this.get()
    const oldFrame = Object.assign({}, frame)
    if (!imageUrl || abortSignal?.aborted) {
      return null;
    }
    image = await loadImageFromURL(imageUrl)
    if (abortSignal?.aborted) {
      return null;
    }
    const item = createDefaultLayer({  
      type: ObjectType.BAZAART_BG,
      width: 0.2,
      guid: guid,
      assetStateId,
      sceneId: sceneId,
    })

    const frameOptions = this.root.frameHandler.getOptions()
    const topOpt = frameOptions.top + frameOptions.height * item.centerPoint.y
    const leftOpt = frameOptions.left + frameOptions.width * item.centerPoint.x
    const baseOptions = {
      angle: 0,
      top: topOpt,
      left: leftOpt,
      width: image.width,
      height: image.height,
      originX: 'center',
      originY: 'center',
      scaleX: frameOptions.width / image.width,
      scaleY: frameOptions.height / image.height,
      flipX: false,
      flipY: false,
      skewX: 0,
      skewY: 0,
      metadata: {},
      selectable: false,
      hasControls: false,
      lockMovementY: true,
      lockMovementX: true,
      strokeWidth: 0,
      padding: 0,
      evented: false,
      type: item.type,
      bazaartGuid: item.bazaartGuid,
      layerAssetStateId: assetStateId,
      id: item.bazaartGuid,
      backgroundIdentifier: backgroundIdentifier,
      source_platform: source_platform,
      version: item.version,
      cropX: 0,
      cropY: 0,
      boundingBox: item.boundingBox,
      effects: {
        hasFillImage: false
      },
      transformation: {
        horizontalFlip: false,
        verticalFlip: false,
      },
      audioVolume: 0,
      currentPrice: 0,
      features: [],
      hasFaceFeatures: false,
      hasPeople: 0,
      imageHeight: 0,
      imageWidth: 0,
      isDeleted: false,
      isDirty: false,
      isHidden: false,
      isInitializing: false,
      isLocked: false,
      isMagicBackgroundLayer: false,
      isPlaceholder: false,
      isShopable: false,
      isTemplateLayer: false,
      itemType: ObjectType.BAZAART_BG,
      lastPrice: 0,
      sourceGraphicsType: "Photos",
      state: 0,
      storeId: 0,
      thumbnailHeight: 0,
      thumbnailWidth: 0,
      videoSpeed: 1,
      zLayer: 0,
      keyFrames: item.keyFrames,
      visible: sceneId === this.root.pagesHandler?.currentScene?.sceneId
    }

    const element = new fabric.StaticImage(image, {
      ...baseOptions,
    })
    let object: fabric.Object = element as any
    // await this.update({ width: oldFrame.width, height: oldFrame.height }, { update: false, zoomToFit: true }) 
    const variationLeft = oldFrame.left - frame.left
    const variationTop = oldFrame.top - frame.top
    this.unsetBackgroundImage(sceneId)
    this.canvas.add(object)
    
    object.clipPath = frame

    this.updateObjectsOnCanvas(this.canvas, frame, variationLeft, variationTop)
    this.scaleImageBgFill(sceneId)
    this.canvas.moveTo(object, 1)
    this.canvas.renderAll()

    return object;
  }

  /**
   * Sets a background image for a specific scene.
   * Used internally by batch operations.
   */
  async setBackgroundImageForScene(
    sceneId: string,
    guidId: string,
    assetStateId: string,
    imageUrl: string,
    backgroundObject: any,
    abortSignal?: any
  ): Promise<fabric.Object | null> {
    if (!imageUrl || abortSignal?.aborted) {
      return null;
    }
    
    const image: any = await loadImageFromURL(imageUrl);
    const frame = this.get();
    const options = this.root.frameHandler.getOptions();
    
    const item = createDefaultLayer({
      type: ObjectType.BAZAART_BG,
      width: 0.2,
      guid: guidId,
      assetStateId,
      sceneId: sceneId,
    });

    const topOpt = options.top + options.height * item.centerPoint.y;
    const leftOpt = options.left + options.width * item.centerPoint.x;
    
    const baseOptions = {
      angle: 0,
      top: topOpt,
      left: leftOpt,
      width: image.width,
      height: image.height,
      originX: 'center',
      originY: 'center',
      scaleX: options.width / image.width,
      scaleY: options.height / image.height,
      flipX: false,
      flipY: false,
      skewX: 0,
      skewY: 0,
      metadata: {},
      selectable: false,
      hasControls: false,
      lockMovementY: true,
      lockMovementX: true,
      strokeWidth: 0,
      padding: 0,
      evented: false,
      type: item.type,
      bazaartGuid: item.bazaartGuid,
      layerAssetStateId: assetStateId,
      id: item.bazaartGuid,
      backgroundIdentifier: backgroundObject.id,
      source_platform: backgroundObject.source_platform,
      version: item.version,
      cropX: 0,
      cropY: 0,
      boundingBox: item.boundingBox,
      effects: { hasFillImage: false },
      transformation: { horizontalFlip: false, verticalFlip: false },
      audioVolume: 0,
      currentPrice: 0,
      features: [],
      hasFaceFeatures: false,
      hasPeople: 0,
      imageHeight: 0,
      imageWidth: 0,
      isDeleted: false,
      isDirty: false,
      isHidden: false,
      isInitializing: false,
      isLocked: false,
      isMagicBackgroundLayer: false,
      isPlaceholder: false,
      isShopable: false,
      isTemplateLayer: false,
      itemType: ObjectType.BAZAART_BG,
      lastPrice: 0,
      sourceGraphicsType: "Photos",
      state: 0,
      storeId: 0,
      thumbnailHeight: 0,
      thumbnailWidth: 0,
      videoSpeed: 1,
      zLayer: 0,
      keyFrames: item.keyFrames,
    };

    const element = new fabric.StaticImage(image, { ...baseOptions });
    const object: fabric.Object = element as any;
    
    this.canvas.add(object);
    object.clipPath = frame;
    this.canvas.moveTo(object, 1);
    
    return object;
  }

  /**
   * Apply the same background to all pages/scenes.
   * Removes existing backgrounds from all scenes and sets the new one.
   */
  async applyBackgroundToAllPages(
    imageUrl: string,
    metadata: { backgroundIdentifier: string, source_platform: string, abortSignal?: any},
  ): Promise<void> {
    const { backgroundIdentifier, source_platform, abortSignal } = metadata
    const scenes = this.root.pagesHandler?.scenes || [];
    const currentSceneId = this.root.pagesHandler?.currentScene?.sceneId;

    this.removeTransparentBg();
    // One URL instance per apply — same href for latest/thumbnail; avoids N×2 URL allocations.
    const bgUrl = new URL(imageUrl);
    const urls = { latest: bgUrl, thumbnail: bgUrl };
    const otherScenes = currentSceneId ? scenes.filter((s) => s.sceneId !== currentSceneId) : scenes
    // Parallel per scene; `parallelBatch` avoids cross-abort on the shared controller.
    // `updateTransaction: false` — Fill wraps this call in transactionHandler.save/end.
    await Promise.all(
      otherScenes.map((scene) =>
        this.setBackgroundImageURL(urls, {
          backgroundIdentifier,
          source_platform,
          abortSignal,
          updateTransaction: false,
          targetSceneId: scene.sceneId,
          parallelBatch: true,
        }),
      ),
    )
    this.canvas.renderAll();
  }

  /**
   * Apply the current frame dimensions (and preset metadata) to every scene,
   * running the same path as a manual resize (canvasSize + adjustLayers) per scene.
   * Restores the active scene when finished.
   */
  applyFrameSizeToAllScenes(): void {
    const pages = this.root.pagesHandler
    if (!pages?.currentScene) {
      return
    }

    const frame = this.get() as fabric.Frame & {
      sizeId?: string
      unit?: string
      icon?: any
    }
    if (!frame) {
      return
    }

    const target = {
      width: frame.width,
      height: frame.height,
      sizeId: frame.sizeId,
      unit: frame.unit || 'px',
      icon: frame.icon,
    }

    for (const scene of pages.scenes) {
      if (scene.sceneId === pages.currentScene.sceneId) {
        continue
      }
      // createTransaction: false — handleApplyAll already wraps this in one transaction;
      // a per-scene transaction would make "Apply all" take N undos instead of one.
      this.update(target, { sceneId: scene.sceneId, createTransaction: false })
      pages.updateSingleScenePreview(scene.sceneId)
    }
  }

  /**
   * Apply background to pages that have a matching background identifier.
   * Only replaces backgrounds that match the specified identifier.
   * 
   * @param matchIdentifier - The backgroundIdentifier to match (e.g., color hex or image ID)
   */
  async applyBackgroundToMatchingPages(
    imageUrl: string,
    backgroundObject: any,
    matchIdentifier: string,
    abortSignal?: any
  ): Promise<void> {
    const scenes = this.root.pagesHandler?.scenes || [];
    const currentSceneId = this.root.pagesHandler?.currentScene?.sceneId;
    
    for (const scene of scenes) {
      const existingBg = this.getBackgroundImageForScene(scene.sceneId) as any;
      
      // Only replace if it matches the target identifier
      if (existingBg && existingBg.backgroundIdentifier === matchIdentifier) {
        this.canvas.remove(existingBg);
        
        // Generate unique IDs for this scene's background
        const sceneGuidId = nanoid();
        const sceneAssetStateId = nanoid();
        
        await this.setBackgroundImageForScene(
          scene.sceneId,
          sceneGuidId,
          sceneAssetStateId,
          imageUrl,
          backgroundObject,
          abortSignal
        );
      }
    }
    
    // Update visibility for current scene and scale background
    if (currentSceneId) {
      this.root.pagesHandler.updateObjectVisibilityForScene(currentSceneId);
    }
    this.scaleImageBgFill();
    this.canvas.renderAll();
  }

  async setPlaceholderBgImage(image: HTMLImageElement) {
    const frame = this.get()
    const item = {
      type: ObjectType.BAZAART_BG,
      centerPoint: {
        x: 0.5,
        y: 0.5,
      },
      sizeOnCanvas: {
        width: 0.2,
      },
      transformation: {
        horizontalFlip: false,
        verticalFlip: false,
      },
      boundingBox: { y: 0, width: 1, height: 1, x: 0 },
      absoluteRotation: 0,
      version: 33,
    }
    const options = this.root.frameHandler.getOptions()
    const topOpt = options.top + options.height * item.centerPoint.y
    const leftOpt = options.left + options.width * item.centerPoint.x
    const baseOptions = {
      angle: 0,
      top: topOpt,
      left: leftOpt,
      width: image.width,
      height: image.height,
      originX: 'center',
      originY: 'center',
      scaleX: options.width / image.width,
      scaleY: options.height / image.height,
      flipX: false,
      flipY: false,
      skewX: 0,
      skewY: 0,
      metadata: {},
      selectable: false,
      hasControls: false,
      lockMovementY: true,
      lockMovementX: true,
      strokeWidth: 0,
      padding: 0,
      evented: false,
      type: item.type,
      version: item.version,
      cropX: 0,
      cropY: 0,
      boundingBox: item.boundingBox,
      effects: {
        hasFillImage: false
      },
      transformation: {
        horizontalFlip: false,
        verticalFlip: false,
      },
      id: "bg-place-holder"
    }

    let blurredImage = this.applyBlurToImage(image);
    const element = new fabric.StaticImage(blurredImage, {
      ...baseOptions,
    })

    // let blurFitler = new BlurFilter(50, 20);
    // blurFitler.repeatEdgePixels = true;
    // blurFitler.multisample = MSAA_QUALITY.HIGH;
    // let filters = [new BasePixiFilter(blurFitler, new Size(0, 0), new Inset(1, 1, 1, 1))]
    // element.applyFilters(filters);


    let object: fabric.Object = element as any
    this.unsetBackgroundImage()
    this.canvas.add(object)
    object.clipPath = frame

    this.scaleImageBgFill()
    this.canvas.moveTo(object, 1)
  }

  removePlaceholderBgImage = () => {
    return new Promise(async resolve => {
      // @ts-ignore
      const currentPlceholder = this.canvas.getObjects().find(object => object.id === "bg-place-holder")

      if (currentPlceholder) {
        this.canvas.remove(currentPlceholder)
        resolve(null)
      } else {
        resolve(null)
      }
    })
  }

  private applyBlurToImage(image: HTMLImageElement): HTMLImageElement {
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');

    let blurSize = 20;

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    let blurFilter = `blur(${blurSize}px)`;
    ctx.filter = blurFilter;

    ctx.drawImage(image, 0, 0);

    if (this.canvasBlurNotSupported()) {
      CanvasBlur(ctx, blurSize);
    }

    let blurredImg = new Image();
    blurredImg.src = canvas.toDataURL();
    return blurredImg;
  }

  private canvasBlurNotSupported(): Boolean {
    let userAgent = navigator.userAgent;
    // Check for Safari
    let isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    // Safari could also be detected by checking the vendor
    let isAppleVendor = /Apple Computer/.test(navigator.vendor);
    // Combine both checks for a more reliable detection
    return (isSafari && isAppleVendor);
  }

  public scaleImageBgFill(sceneId?: string) {
    const currentBackgroundImage = this.getBackgroundImage(sceneId) as any
    const scene = sceneId ? this.root.pagesHandler.getSceneById(sceneId) : this.root.pagesHandler.currentScene
    const { width, height } = scene ? scene.canvasSize : this.root.frameHandler.getOptions()
    if (currentBackgroundImage) {
      let scaleX = width / currentBackgroundImage.width
      let scaleY = height / currentBackgroundImage.height
      const scaleMax = Math.max(scaleX, scaleY)
      currentBackgroundImage.set({
        scaleY: scaleMax,
        scaleX: scaleMax,
        _originalScaleX: scaleMax,
        _originalScaleY: scaleMax
      })

      currentBackgroundImage.center()
    }
  }

  /**
   * Removes the background image for a scene. When `sceneId` is omitted, uses the current scene
   * (same as {@link getBackgroundImage} with no argument).
   */
  unsetBackgroundImage(sceneId?: string | null): Promise<fabric.StaticImage | null> {
    return new Promise(async resolve => {
      const currentBackgroundImage = this.getBackgroundImage(sceneId ?? this.root.pagesHandler?.currentScene?.sceneId)

      if (currentBackgroundImage) {
        this.canvas.remove(currentBackgroundImage)
        resolve(null)
      } else {
        resolve(null)
      }
    })
  }

  sendToBack = (id?: string) => {
    let refObject = this.canvas.getActiveObject()
    const objects = this.canvas.getObjects()
    const backgroundImage = objects.find(o => o.type === ObjectType.BAZAART_BG)
    if (id) {
      refObject = this.findOneById(id)
    }
    // const indexx =
    if (refObject) {
      if (backgroundImage) {
        refObject.moveTo(3)
      } else {
        refObject.moveTo(2)
      }
    }
  }

  public findOneById = (id: string) => {
    const objects = this.findById(id)
    return objects[0]
  }

  public findById = (id: string) => {
    const objects = this.canvas.getObjects()
    const object = this.findByIdInObjecs(id, objects)
    return [object]
  }

  public findByIdInObjecs = (id: string, objects: fabric.Object[]): any => {
    let item = null

    for (const object of objects) {
      // @ts-ignore
      if (object.id === id) {
        item = object
        break
      }
      if (object.type === 'group') {
        // @ts-ignore
        const itemInGroup = this.findByIdInObjecs(id, object._objects)
        if (itemInGroup) {
          item = itemInGroup
          break
        }
      }
    }
    return item
  }

  create = _options => {
    const frame = new fabric.Frame({
      id: '',
      name: 'Initial Frame',
      fill: lightTheme.colors.white,
      hoverCursor: 'default',
      ...this.options,
    })
    this.canvas.add(frame)
    frame.center()
  }

  /**
   * Gets the background image for a specific scene.
   * For per-scene backgrounds, returns only the background that belongs to the current scene.
   * For legacy backgrounds (no keyFrames), returns the global background for backward compatibility.
   */
  getBackgroundImage = (sceneId?: string) => {
    const currentSceneId = sceneId ? sceneId : this.root.pagesHandler?.currentScene?.sceneId;
    return this.canvas.getObjects().reverse().find((object: any) => {
      if (object.type !== ObjectType.BAZAART_BG) return false;
      // If object has keyFrames, check if it belongs to current scene
      if (object.keyFrames && Object.keys(object.keyFrames).length > 0) {
        return !!object.keyFrames[currentSceneId];
      }
      // Legacy: objects without keyFrames are global (for backward compatibility)
      return true;
    });
  }

  /**
   * Gets the background image for a specific scene.
   * Used by batch operations to find backgrounds on other scenes.
   */
  getBackgroundImageForScene = (sceneId: string) => {
    return this.canvas.getObjects().reverse().find((object: any) => {
      if (object.type !== ObjectType.BAZAART_BG) return false;
      if (object.keyFrames && Object.keys(object.keyFrames).length > 0) {
        return !!object.keyFrames[sceneId];
      }
      // Legacy backgrounds without keyFrames are considered global
      return true;
    });
  }

  /**
   * Gets all background images across all scenes.
   */
  getAllBackgroundImages = () => {
    return this.canvas.getObjects().filter((object: any) => object.type === ObjectType.BAZAART_BG);
  }

  getBackgroundImageSrc = () => {
    const bgImage = this.getBackgroundImage() as any
    return bgImage ? bgImage.getElement().currentSrc : null
  }

  reset = () => {
    // const frame = this.get()
    // frame.set('fill', defaultFrameOptions.fill)
  }

  setSelectionBorder = () => {
    // const frame = this.root.frameHandler.get()
    // frame.setSelectionBorder()
  }

  getOptions = (): FrameOptions => {
    const frame = this.get()
    // @ts-ignore
    return frame.toJSON(this.root.propertiesToInclude)
  }

  scaleDimension = options => {
    const { width, height } = options
    return {
      height: height,
      width: width,
    }
  }

  public getFitRatio() {
    const options = this.getOptions()
    return this.getFitRatioForDimensions(options.width, options.height)
  }

  public getFitRatioForDimensions(width: number, height: number): number {
    const canvasWidth = window.innerWidth - this.FRAME_PADDING_W - this.FRAME_PADDING_LEFT - this.FRAME_PADDING_RIGHT
    const canvasHeight = window.innerHeight - this.FRAME_PADDING_H - LayoutUtils.NAVBAR_HEIGHT - this.DEFAULT_HEIGHT_EDIT_WITH_TEXTBOX - this.MARGIN_BOTTOM_WHEN_TEXTBOX_OPEN
    return Math.min(canvasWidth / width, canvasHeight / height)
  }

  public setOriginalObjectsState = () => {
    const frame = this.get()
    this.baseFrameWidth = frame.width
    this.baseFrameHeight = frame.height
    this.baseFrameCenterX = frame.left + frame.width / 2
    this.baseFrameCenterY = frame.top + frame.height / 2
    this.originalObjectsStates = this.canvas.getObjects().map((object: any) => ({
      id: object.id,
      left: object.left,
      top: object.top,
      scaleX: object.scaleX,
      scaleY: object.scaleY,
      type: object.type,
      _originalScaleX: object._originalScaleX,
      _originalScaleY: object._originalScaleY
    }))
  }

  public adjustLayers = (newFrameWidth: number, newFrameHeight: number, sceneId: string = null) => {
    /* ── 1. frame-level numbers ─────────────────────────────── */
    const { left, top } = this.getOptions();
    const frameCenter = {
      x: left + newFrameWidth  * 0.5,
      y: top  + newFrameHeight * 0.5,
    };
  
    const scaleX          = newFrameWidth  / this.baseFrameWidth;
    const scaleY          = newFrameHeight / this.baseFrameHeight;
    const uniformScale    = Math.min(scaleX, scaleY);          // keep aspect
    const oldRatio        = this.baseFrameWidth  / this.baseFrameHeight;
    const newRatio        = newFrameWidth       / newFrameHeight;
  
    /* ── 2. helper that resizes a single object ─────────────── */
    const resizeObject = (obj: any) => {
      const original = this.originalObjectsStates.find(o => o.id === obj.id);
      if (!original) return;
  
      // clip + position
      obj.clipPath = this.get();
      obj.left = frameCenter.x + (original.left - this.baseFrameCenterX) * uniformScale;
      obj.top  = frameCenter.y + (original.top  - this.baseFrameCenterY)  * uniformScale;
  
      // ensure sizeOnCanvas exists
      if (!obj.sizeOnCanvas) {
        this.root.objectsHandler.updateSizeOnCanvas(
          obj,
          new Size(this.baseFrameWidth, this.baseFrameHeight)
        );
      }
      // width tweak when aspect changes
      if (this.baseFrameWidth < this.baseFrameHeight || this.baseFrameWidth < newFrameWidth) {
        obj.sizeOnCanvas.width *= oldRatio / newRatio;
      }

      // scale
      if ([ObjectType.BAZAART_BG, ObjectType.BAZAART_OVERLAY].includes(obj.type)) {
        const coverScale = Math.max(
          newFrameWidth  / obj.width,
          newFrameHeight / obj.height
        );
        obj.scaleX = obj.scaleY = obj._originalScaleX = obj._originalScaleY = coverScale;
      } else {
        obj.scaleX         = original.scaleX         * uniformScale;
        obj.scaleY         = original.scaleY         * uniformScale;
        obj._originalScaleX = original._originalScaleX * uniformScale;
        obj._originalScaleY = original._originalScaleY * uniformScale;
      }
  
      obj.setCoords();
    };
  
    /* ── 3. walk over every canvas object ───────────────────── */
    const currentSceneId = sceneId ?? this.root.pagesHandler?.currentScene?.sceneId;
    
    this.root.canvasHandler.canvas.forEachObject((obj: any) => {
      if (obj.type === ObjectType.FRAME) return;
      
      // Only adjust objects belonging to current scene
      // If object has keyFrames defined, only adjust if it belongs to current scene
      // Objects without keyFrames (legacy) appear on all scenes and should be adjusted
      if (currentSceneId && obj.keyFrames && Object.keys(obj.keyFrames).length > 0 && Object.keys(obj.keyFrames)[0] !== currentSceneId && !obj.keyFrames[currentSceneId]) {
        return; // Skip objects not belonging to current scene
      }

      // Backgrounds need their image re-loaded asynchronously
      if (obj.type === ObjectType.BAZAART_BG) {
        setTimeout(async () => {
          const repo   = MediaImageRepositoryProvider.getEditingProjectInstance();
          const base64 = await repo.getImage(obj.id, obj.layerAssetStateId, MediaImageType.original);
          const image  = await repo._mediaImageRepositoryProcessing.loadImage(base64);
  
          obj.setElement(image);
          resizeObject(obj);
  
          await CanvasImageRenderer.getInstance().render(
            obj,
            new Size(newFrameWidth, newFrameHeight),
            store.getState().editor.imageElements.imageElements,
            null,
            true
          );
          this.root.canvasHandler.canvas.renderAll();
        });
      } else {
        resizeObject(obj);
      }
    });
  
    /* ── 4. one final redraw ────────────────────────────────── */
    this.root.canvasHandler.canvas.renderAll();
  };
  

  setImageLatest = async (object, canvas) => {
    let image = null;
    let imageUrl = await MediaImageRepositoryProvider.getEditingProjectInstance().getImage(object.bazaartGuid, object.layerAssetStateId, MediaImageType.latest);

    if (!imageUrl) {
      return
    }

    image = await loadImageFromURL(imageUrl);
    await this.setImageProperties(object, image, canvas);
  }

  setImageProperties = async (object, image, canvas) => {
    await object.setElement(image);
    object.set({
      width: image.width,
      height: image.height,
    });
   
    this.scaleImageBgFill();

    await CanvasImageRenderer.getInstance().render(
      object,
      this.root.frameHandler.getSize(),
      store.getState().editor.imageElements.imageElements,
      null,
      true
    )
  }

  setBackgroundIdentifier(object) {
    if (!object.backgroundIdentifier) {
      return
    }
    let splitBackgroundIdentifier = [];
    if (typeof object.backgroundIdentifier === "number" && !isNaN(object.backgroundIdentifier)) {
      splitBackgroundIdentifier[0] = object.backgroundIdentifier;
    } else {
      splitBackgroundIdentifier = object.backgroundIdentifier.split("_"); 
    }
    splitBackgroundIdentifier[1] === 'color' && splitBackgroundIdentifier[0] !== "" ? this.setBackgroundColor(splitBackgroundIdentifier[0])
      : this.editorEventManager.emit('background:changed', {})
  }

  public updateLayersCoords = debounce(() => {
    this.editorEventManager.emit('scroll:changed')
    this.canvas.forEachObject(obj => obj.setCoords())
  }, 100)

  getSize(): Size {
    const frame = this.get();
    if (!frame) {
      return new Size(0, 0);
    }
    return new Size(frame.width, frame.height);
  }

  getCenter(): Point {
    let state = this.get();
    let center = new Point(state.left + state.width / 2, state.top + state.height / 2);
    return center;
  }

  handleTransparentBgAfterRemoveBg = () => {
    const allLayers = this.canvas.getObjects()
    if (allLayers.length === 2) { // 1 frame + 1 layer removebg
      this.setTransparentBg(false)
    } else if (allLayers.length === 3) {
      const secondLayer = allLayers[1] // Background layer always is second layers
      // @ts-ignore
      if (secondLayer.type === ObjectType.BAZAART_BG && secondLayer.backgroundIdentifier.toLowerCase() === lightTheme.colors.white.toLowerCase()) { //
        this.setTransparentBg(false)
      }
    }
  }

  static convertUnitValue = _convertUnitValue
}

export default FrameHandler